// src/cockpit4/workspaces/ProjectsWorkspace.tsx
// ============================================
// Projects Workspace - Runtime Catalog（Program A 重写）
//
// 不再是看板，而是 Runtime Catalog：
//   - 每个产品都是一个可运行的 Runtime（Hotel / Gold Factory / AI Destiny / Marketing OS）
//   - 加上已有的 Quant Runtime 和 Growth Engine（GEO+DSO）
//   - 点击「进入产品」→ 调用 Runtime.execute('launch') → 弹出产品详情 Modal
//
// 数据来源：RuntimeManager.query(RuntimeName.Xxx, { type: 'catalog' })
// 不再使用 MOCK_PROJECTS，全部为真实 Runtime 查询。
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderKanban,
  PlayCircle,
  Pause,
  CheckCircle2,
  CircleDashed,
  Shield,
  User,
  ArrowRight,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
  ExternalLink,
  TrendingUp,
  Factory,
  Brain,
  Megaphone,
  Rocket,
  Activity,
  Wrench,
  LayoutGrid,
  CheckSquare,
  Bot,
  Users,
  ShieldAlert,
  ShieldCheck,
  BookOpen,
  Store,
  Boxes,
  // P2 标签图标
  Target,
  GitBranch,
  ClipboardCheck,
  ChevronDown,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
// Agent Mesh 管控台：经验飞轮 + Agent 编排层的可见管理面板
import AgentMeshConsole from "../components/AgentMeshConsole";
import type {
  RuntimeInfo,
  RuntimeHealth,
  RuntimeStatus,
} from "../../types/runtime";
import type { ProductCatalogInfo } from "../../runtime/ProductRuntimes/types";
// P0 新增：项目中心三个子组件
import ProjectDashboard from "../components/ProjectDashboard";
import TaskBoard from "../components/TaskBoard";
import AIProjectAssistant from "../components/AIProjectAssistant";
// P1 新增：项目中心四个子组件（资源/风险/质量/文档）
import ResourceManager from "../components/ResourceManager";
import RiskManager from "../components/RiskManager";
import QualityManager from "../components/QualityManager";
import ProjectDocs from "../components/ProjectDocs";
// P2-功能8：项目组合管理
import ProjectPortfolioManager from "../components/ProjectPortfolioManager";
// P2-功能9：战略对齐
import StrategicAlignment from "../components/StrategicAlignment";
// P2-功能10：项目复盘与持续改进
import ProjectRetrospective from "../components/ProjectRetrospective";
// P2 任务：站内量化工作台（实时行情 + 外部工作站入口）
import QuantTradingPanel from "../components/QuantTradingPanel";
// v5.0：AI 超市产品卡片货架
import AIMarketplacePanel from "../components/AIMarketplacePanel";

// ============================================
// Catalog 项类型（融合产品 Runtime + Quant + GrowthEngine）
// ============================================

interface CatalogItem {
  productId: string;
  runtimeName: RuntimeName;
  displayName: string;
  subtitle: string;
  description: string;
  category: "saas" | "factory" | "knowledge" | "marketing" | "quant" | "growth" | "agent_tool";
  runtimeStatus: "running" | "open" | "beta" | "planning" | "paused";
  owner: string;
  expectedRevenue: number;
  progress: number;
  riskLevel: "low" | "medium" | "high";
  capabilities: string[];
  launchUrl: string;
  vaultPath?: string;
  milestones: Array<{
    name: string;
    dueDate: string;
    status: "completed" | "in_progress" | "pending";
  }>;
  updatedAt?: string;
}

// ============================================
// 配置
// ============================================

const CATEGORY_CONFIG: Record<
  CatalogItem["category"],
  { label: string; color: string; icon: typeof Factory }
> = {
  saas: { label: "SaaS", color: "#38BDF8", icon: Rocket },
  factory: { label: "工厂", color: "#FBBF24", icon: Factory },
  knowledge: { label: "国学", color: "#A78BFA", icon: Brain },
  marketing: { label: "营销", color: "#34D399", icon: Megaphone },
  // 量化分类：含「黄金外汇量化」「股票期权量化」两个子产品，通过 productId 区分
  quant: { label: "量化 · 黄金外汇/股票期权", color: "#F472B6", icon: TrendingUp },
  growth: { label: "增长", color: "#60A5FA", icon: Activity },
  agent_tool: { label: "Agent工具", color: "#FB923C", icon: Wrench },
};

const STATUS_CONFIG: Record<
  CatalogItem["runtimeStatus"],
  { label: string; color: string; icon: typeof PlayCircle; dotColor: string }
> = {
  running: { label: "运行中", color: "#10B981", icon: PlayCircle, dotColor: "bg-emerald-400" },
  open: { label: "已开放", color: "#3B82F6", icon: CheckCircle2, dotColor: "bg-blue-400" },
  beta: { label: "Beta", color: "#A78BFA", icon: Sparkles, dotColor: "bg-violet-400" },
  planning: { label: "规划中", color: "#9CA3AF", icon: CircleDashed, dotColor: "bg-gray-400" },
  paused: { label: "已暂停", color: "#F59E0B", icon: Pause, dotColor: "bg-amber-400" },
};

const RISK_CONFIG: Record<
  CatalogItem["riskLevel"],
  { label: string; color: "emerald" | "amber" | "red" }
> = {
  low: { label: "低风险", color: "emerald" },
  medium: { label: "中风险", color: "amber" },
  high: { label: "高风险", color: "red" },
};

// Runtime Health 颜色映射（任务3：色条联动 + 任务2：实测 Modal）
const HEALTH_COLOR: Record<RuntimeHealth, string> = {
  healthy: "#34D399",
  degraded: "#FBBF24",
  unhealthy: "#F87171",
  unknown: "#9CA3AF",
};

const HEALTH_LABEL: Record<RuntimeHealth, string> = {
  healthy: "健康",
  degraded: "降级",
  unhealthy: "异常",
  unknown: "未知",
};

// Runtime Status 中文标签（任务2：实测 Modal 展示）
const RUNTIME_STATUS_LABEL: Record<RuntimeStatus, string> = {
  idle: "空闲",
  loading: "加载中",
  ready: "就绪",
  error: "错误",
  stopped: "已停止",
};

// 需要查询的 Runtime 列表（产品 Runtime + Quant + GrowthEngine）
const CATALOG_RUNTIMES: Array<{ name: RuntimeName; fallback: CatalogItem }> = [
  {
    name: RuntimeName.Hotel,
    fallback: {
      productId: "hotel",
      runtimeName: RuntimeName.Hotel,
      displayName: "Hotel SaaS",
      subtitle: "酒店运营 Runtime",
      description: "加载中...",
      category: "saas",
      runtimeStatus: "planning",
      owner: "—",
      expectedRevenue: 0,
      progress: 0,
      riskLevel: "medium",
      capabilities: [],
      launchUrl: "/cockpit/hotel",
      milestones: [],
    },
  },
  {
    name: RuntimeName.GoldFactory,
    fallback: {
      productId: "gold_factory",
      runtimeName: RuntimeName.GoldFactory,
      displayName: "Gold Factory · 黄金量化",
      subtitle: "黄金工厂 Runtime（与黄金外汇量化统一入口）",
      description:
        "黄金量化（Gold Factory）一体化平台：多策略回测、实时信号、风控止盈止损、跨市场套利，覆盖 XAU/USD、主要货币对。与「黄金外汇量化」为同一产品体系，统一入口为 trade.xshetech.com。",
      category: "factory",
      runtimeStatus: "running",
      owner: "Quant Team",
      expectedRevenue: 0,
      progress: 60,
      riskLevel: "medium",
      capabilities: ["多策略回测", "实时信号", "风控止盈止损", "跨市场套利", "自动化下单"],
      launchUrl: "https://trade.xshetech.com/?product=gold_forex",
      vaultPath: "05_GoldFactory_Quant/_INDEX.md",
      milestones: [
        { name: "策略回测引擎上线", dueDate: "2026-08-10", status: "completed" },
        { name: "实时信号接入", dueDate: "2026-08-25", status: "in_progress" },
        { name: "自动化下单打通", dueDate: "2026-09-15", status: "pending" },
      ],
    },
  },
  {
    name: RuntimeName.AIDestiny,
    fallback: {
      productId: "ai_destiny",
      runtimeName: RuntimeName.AIDestiny,
      displayName: "AI Destiny",
      subtitle: "国学命理 Runtime",
      description: "加载中...",
      category: "knowledge",
      runtimeStatus: "planning",
      owner: "—",
      expectedRevenue: 0,
      progress: 0,
      riskLevel: "medium",
      capabilities: [],
      launchUrl: "https://xuanji.xshetech.com", // 国学命理二级域名（xuanji.xshetech.com 玄机命理）
      milestones: [],
    },
  },
  {
    name: RuntimeName.MarketingOS,
    fallback: {
      productId: "marketing_os",
      runtimeName: RuntimeName.MarketingOS,
      displayName: "Marketing OS",
      subtitle: "营销运营 Runtime",
      description: "加载中...",
      category: "marketing",
      runtimeStatus: "planning",
      owner: "—",
      expectedRevenue: 0,
      progress: 0,
      riskLevel: "medium",
      capabilities: [],
      launchUrl: "https://os.xshetech.com",
      milestones: [],
    },
  },
  // Agent 工具类产品（静态展示，不依赖 Runtime）
  {
    name: RuntimeName.CozeWorkflowGen,
    fallback: {
      productId: "viral_analysis_engine",
      runtimeName: RuntimeName.CozeWorkflowGen,
      displayName: "爆款分析引擎",
      subtitle: "Viral Analysis Engine",
      description:
        "XEAOS Coze 工作流编译器：自动生成 Coze 工作流配置 → 采集赛道爆款数据 → LLM 提取共性规律 → 输出 Content Brief（脚本模板/标题公式/封面规范/标签组合），直接输入内容工厂。",
      category: "growth",
      runtimeStatus: "beta",
      owner: "Hermes",
      expectedRevenue: 0,
      progress: 60,
      riskLevel: "low",
      capabilities: ["Coze工作流编译", "爆款共性提取", "Content Brief生成", "内容工厂对接", "多平台模板"],
      launchUrl: "/cockpit/viral-analysis",
      vaultPath: "99_Core/Coze_Templates/",
      milestones: [
        { name: "Coze模板定义 (douyin)", dueDate: "2026-08-05", status: "completed" },
        { name: "CozeWorkflowGen Runtime", dueDate: "2026-08-05", status: "completed" },
        { name: "CozeExecutor + Vault回写", dueDate: "2026-08-05", status: "completed" },
        { name: "内容工厂Content Brief对接", dueDate: "2026-08-07", status: "in_progress" },
        { name: "小红书模板上线", dueDate: "2026-08-08", status: "pending" },
      ],
    },
  },
  // 量化产品线（静态展示，外部链接跳转）
  {
    name: RuntimeName.Quant, // 借用 Quant Runtime 作为黄金外汇量化占位
    fallback: {
      productId: "gold_forex_quant",
      runtimeName: RuntimeName.Quant,
      displayName: "黄金外汇量化",
      subtitle: "Gold & Forex Quant",
      description:
        "黄金与外汇市场量化交易系统：多策略回测、实时信号生成、风控止盈止损、跨市场套利。覆盖 XAU/USD、主要货币对，支持自动化下单与多账户跟单。",
      category: "quant",
      runtimeStatus: "running",
      owner: "Quant Team",
      expectedRevenue: 0,
      progress: 60,
      riskLevel: "medium",
      capabilities: ["多策略回测", "实时信号", "风控止盈止损", "跨市场套利", "自动化下单"],
      launchUrl: "https://trade.xshetech.com/?product=gold_forex", // 黄金外汇量化二级域名
      milestones: [
        { name: "策略回测引擎上线", dueDate: "2026-08-10", status: "completed" },
        { name: "实时信号接入", dueDate: "2026-08-25", status: "in_progress" },
        { name: "自动化下单打通", dueDate: "2026-09-15", status: "pending" },
      ],
    },
  },
  {
    name: RuntimeName.Quant, // 借用 Quant Runtime 作为股票期权量化占位
    fallback: {
      productId: "stock_options_quant",
      runtimeName: RuntimeName.Quant,
      displayName: "股票期权量化",
      subtitle: "Stock & Options Quant",
      description:
        "A股 / 港股 / 美股与期权量化交易系统：多因子选股、期权定价与希腊字母风控、波动率套利、日内 T+0 策略。支持策略组合管理与实盘跟单。",
      category: "quant",
      runtimeStatus: "running",
      owner: "Quant Team",
      expectedRevenue: 0,
      progress: 55,
      riskLevel: "medium",
      capabilities: ["多因子选股", "期权定价", "希腊字母风控", "波动率套利", "日内T+0"],
      launchUrl: "https://trade.xshetech.com/?product=stock_options", // 股票期权量化二级域名
      milestones: [
        { name: "多因子模型上线", dueDate: "2026-08-12", status: "completed" },
        { name: "期权定价引擎", dueDate: "2026-08-30", status: "in_progress" },
        { name: "实盘跟单打通", dueDate: "2026-09-20", status: "pending" },
      ],
    },
  },
];

// ============================================
// 主组件
// ============================================

export default function ProjectsWorkspace() {
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    item: CatalogItem;
    content: string;
    loading: boolean;
  } | null>(null);
  const [launching, setLaunching] = useState<string | null>(null);
  // 任务1：Runtime 可调用标记（productId → 是否真实注册）
  const [runtimeAvailability, setRuntimeAvailability] = useState<
    Record<string, boolean>
  >({});
  // 任务3：Runtime Health 联动（productId → health）
  const [runtimeHealth, setRuntimeHealth] = useState<
    Record<string, RuntimeHealth>
  >({});
  // 任务2：Runtime 实测 Modal 状态
  const [probe, setProbe] = useState<{
    item: CatalogItem;
    loading: boolean;
    info: RuntimeInfo | null;
    error: string | null;
  } | null>(null);
  // P0 + P1 区块切换：catalog / dashboard / tasks / assistant（P0） + resources / risks / quality / docs（P1）
  const [activeSection, setActiveSection] = useState<
    | "products"
    | "catalog"
    | "dashboard"
    | "tasks"
    | "assistant"
    | "resources"
    | "risks"
    | "quality"
    | "docs"
    | "portfolio"
    | "alignment"
    | "retrospective"
    | "quant"
    | "agentmesh"
  >("products");
  // v6 收敛：低频功能收进"更多▾"下拉
  const [showMoreSections, setShowMoreSections] = useState(false);

  // 等待 RuntimeManager 初始化完成
  const waitForRuntime = useCallback(
    (maxRetries = 20, intervalMs = 300): Promise<boolean> =>
      RuntimeManager.waitForRuntime({ timeoutMs: maxRetries * intervalMs, intervalMs }),
    []
  );

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const ready = await waitForRuntime();
      if (!ready) {
        setError("RuntimeManager 未就绪，请稍后重试");
        setCatalog(CATALOG_RUNTIMES.map((c) => c.fallback));
        // 未就绪时清空可用性 / health 表
        setRuntimeAvailability({});
        setRuntimeHealth({});
        return;
      }

      // 并行查询所有产品 Runtime 的 Catalog + 检查是否真实注册 + 获取 health
      // 注意：RuntimeManager.query 返回 RuntimeResponse，其 data 字段是 ProductResult<ProductCatalogInfo>
      // 需要再解包一层：resp.data.data 才是真正的 ProductCatalogInfo
      const results = await Promise.all(
        CATALOG_RUNTIMES.map(async ({ name, fallback }) => {
          // 任务1：同步检查 Runtime 是否真实注册
          const available = RuntimeManager.hasRuntime(name);

          let item: CatalogItem = fallback;
          let health: RuntimeHealth | undefined;

          // 任务3：注册时同步获取 RuntimeInfo，提取 health 用于色条联动
          if (available) {
            try {
              const info = RuntimeManager.getRuntime(name).getInfo();
              health = info.health;
            } catch {
              health = undefined;
            }
          }

          try {
            const resp = await RuntimeManager.query(name, { type: "catalog" });
            if (resp?.success && resp.data) {
              // resp.data 是 ProductResult<ProductCatalogInfo> = { success, data, error, productId, timestamp }
              const productResult = resp.data as {
                success: boolean;
                data?: ProductCatalogInfo;
                error?: string;
              };
              if (productResult.success && productResult.data) {
                const c = productResult.data;
                item = {
                  productId: c.productId,
                  runtimeName: name,
                  displayName: c.displayName,
                  subtitle: c.subtitle,
                  description: c.description,
                  category: c.category,
                  runtimeStatus: c.runtimeStatus,
                  owner: c.owner,
                  expectedRevenue: c.expectedRevenue,
                  progress: c.progress,
                  riskLevel: c.riskLevel,
                  capabilities: c.capabilities,
                  launchUrl: c.launchUrl,
                  vaultPath: c.vaultPath,
                  milestones: c.milestones,
                  updatedAt: c.updatedAt,
                } as CatalogItem;
              }
            }
          } catch {
            // catalog 查询失败，使用 fallback；available / health 已在外层设置
          }

          return { item, available, health };
        })
      );

      setCatalog(results.map((r) => r.item));

      // 任务1：收集 availability 表
      const availMap: Record<string, boolean> = {};
      // 任务3：收集 health 表
      const healthMap: Record<string, RuntimeHealth> = {};
      results.forEach((r) => {
        availMap[r.item.productId] = r.available;
        if (r.health) {
          healthMap[r.item.productId] = r.health;
        }
      });
      setRuntimeAvailability(availMap);
      setRuntimeHealth(healthMap);
    } catch (err: any) {
      setError(err?.message || String(err));
      setCatalog(CATALOG_RUNTIMES.map((c) => c.fallback));
      setRuntimeAvailability({});
      setRuntimeHealth({});
    } finally {
      setLoading(false);
    }
  }, [waitForRuntime]);

  // 刷新单个产品（调用 execute('refresh') 重新加载 Vault）
  const refreshProduct = useCallback(async (item: CatalogItem) => {
    setRefreshing(true);
    try {
      await RuntimeManager.execute(item.runtimeName, "refresh");
      await loadCatalog();
    } catch (err) {
      console.warn(`[ProjectsWorkspace] refresh ${item.productId} failed:`, err);
    } finally {
      setRefreshing(false);
    }
  }, [loadCatalog]);

  // 点击「进入产品」→ 外部链接用 window.open 跳转，内部路由走 Modal 预览
  const launchProduct = useCallback(async (item: CatalogItem) => {
    // 如果 launchUrl 是外部地址（http/https 开头），直接新窗口打开，不再弹 Modal
    if (/^https?:\/\//i.test(item.launchUrl)) {
      window.open(item.launchUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // 内部路由：保持原有 Modal 预览逻辑（读取 Vault 项目文件）
    setLaunching(item.productId);
    setPreview({ item, content: "", loading: true });
    try {
      // 1. 调用 Runtime.execute('launch') 获取 launch handle
      // 注意：RuntimeManager.execute 返回 RuntimeResponse，其 data 字段是 ProductResult<ProductLaunchHandle>
      const launchResp = await RuntimeManager.execute(item.runtimeName, "launch");
      if (!launchResp?.success) {
        setPreview({
          item,
          content: `启动失败：${launchResp?.error || "未知错误"}`,
          loading: false,
        });
        return;
      }
      // 解包 ProductResult
      const launchResult = launchResp.data as {
        success: boolean;
        data?: { productId: string; launchMode: string; target: string; launchedAt: string };
        error?: string;
      };
      if (!launchResult?.success) {
        setPreview({
          item,
          content: `启动失败：${launchResult?.error || "Runtime 执行失败"}`,
          loading: false,
        });
        return;
      }

      // 2. 读取 Vault 项目文件内容（如果有 vaultPath）
      if (item.vaultPath) {
        try {
          const resp = await fetch(
            `/vault-api/read?path=${encodeURIComponent(item.vaultPath)}`
          );
          if (resp.ok) {
            const json = await resp.json();
            if (json?.success && typeof json.content === "string") {
              setPreview({ item, content: json.content, loading: false });
              return;
            }
          }
        } catch {
          // Vault 读取失败，继续展示 Catalog 元信息
        }
      }

      // 3. Vault 不可达时，展示 Catalog 元信息作为预览
      const fallbackContent = `# ${item.displayName}\n\n> ${item.subtitle}\n\n${item.description}\n\n## 能力\n\n${item.capabilities.map((c) => `- ${c}`).join("\n")}\n\n## 里程碑\n\n${item.milestones.map((m) => `- [${m.status === "completed" ? "x" : " "}] ${m.name}（${m.dueDate}）`).join("\n")}\n\n---\n\n*由 Runtime Catalog 自动生成 · vaultPath: ${item.vaultPath || "未配置"}*`;
      setPreview({ item, content: fallbackContent, loading: false });
    } catch (err: any) {
      setPreview({
        item,
        content: `启动失败：${err?.message || String(err)}`,
        loading: false,
      });
    } finally {
      setLaunching(null);
    }
  }, []);

  // 任务2：实测 Runtime - 调用 RuntimeManager.getRuntime(name).getInfo() 获取真实 RuntimeInfo
  const probeRuntime = useCallback(async (item: CatalogItem) => {
    setProbe({ item, loading: true, info: null, error: null });
    try {
      // 先检查是否注册
      if (!RuntimeManager.hasRuntime(item.runtimeName)) {
        setProbe({
          item,
          loading: false,
          info: null,
          error: `Runtime "${item.runtimeName}" 未注册`,
        });
        return;
      }
      // 同步调用 getInfo() 获取真实 RuntimeInfo
      const runtime = RuntimeManager.getRuntime(item.runtimeName);
      const info = runtime.getInfo();
      setProbe({ item, loading: false, info, error: null });
    } catch (err: any) {
      setProbe({
        item,
        loading: false,
        info: null,
        error: err?.message || String(err),
      });
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // ============================================
  // KPI 计算
  // ============================================
  const kpi = {
    total: catalog.length,
    running: catalog.filter((c) => c.runtimeStatus === "running").length,
    totalRevenue: catalog.reduce((sum, c) => sum + (c.expectedRevenue || 0), 0),
    avgProgress:
      catalog.length > 0
        ? Math.round(
            catalog.reduce((sum, c) => sum + (c.progress || 0), 0) / catalog.length
          )
        : 0,
  };

  // ============================================
  // 渲染
  // ============================================
  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
              <FolderKanban size={14} className="text-purple-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">
                产品目录
              </h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                产品矩阵 · 点进去直接进入产品
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadCatalog}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-white/[0.06] hover:bg-white/[0.04] text-[10px] font-mono text-gray-400 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <Loader2 size={10} className="animate-spin" />
              ) : (
                <RefreshCw size={10} />
              )}
              刷新
            </button>
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
              {kpi.total} 个产品 · {kpi.running} 运行中
            </span>
          </div>
        </div>

        {/* v6 收敛：核心 P0 6 项平铺 + P1/P2 7 项收进"更多▾" */}
        <div className="relative z-30 flex items-center gap-1 px-4 py-2 border-b border-white/[0.06] bg-white/[0.01] flex-wrap">
          {/* 核心标签组（项目主链路） */}
          <span className="text-[8px] font-mono text-gray-600 uppercase tracking-wider mr-1">
            核心
          </span>
          {(
            [
              { id: "products", label: "产品货架", icon: Store },
              { id: "catalog", label: "Runtime目录", icon: FolderKanban },
              { id: "dashboard", label: "项目看板", icon: LayoutGrid },
              { id: "tasks", label: "任务看板", icon: CheckSquare },
              { id: "assistant", label: "AI助理", icon: Bot },
              { id: "quant", label: "量化工作台", icon: TrendingUp },
              { id: "agentmesh", label: "Agent管控台", icon: Boxes },
            ] as const
          ).map((tab) => {
            const TIcon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                  isActive
                    ? "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                    : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <TIcon size={11} />
                {tab.label}
              </button>
            );
          })}

          {/* 更多▾ 下拉 */}
          <div className="relative">
            <button
              onClick={() => setShowMoreSections(!showMoreSections)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                showMoreSections
                  ? "bg-white/[0.06] text-white border border-white/[0.12]"
                  : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
              }`}
            >
              <span>更多</span>
              <ChevronDown size={10} className={showMoreSections ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {showMoreSections && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMoreSections(false)} />
                <div className="absolute top-full left-0 mt-1 z-40 min-w-[160px] rounded-lg border border-white/[0.1] bg-[#0d1220] p-1 shadow-2xl">
                  {(
                    [
                      { id: "resources", label: "资源管理", icon: Users },
                      { id: "risks", label: "风险管理", icon: ShieldAlert },
                      { id: "quality", label: "质量管理", icon: ShieldCheck },
                      { id: "docs", label: "项目文档", icon: BookOpen },
                      { id: "portfolio", label: "组合管理", icon: Target },
                      { id: "alignment", label: "战略对齐", icon: GitBranch },
                      { id: "retrospective", label: "项目复盘", icon: ClipboardCheck },
                    ] as const
                  ).map((tab) => {
                    const TIcon = tab.icon;
                    const isActive = activeSection === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveSection(tab.id);
                          setShowMoreSections(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] font-mono transition-colors ${
                          isActive
                            ? "text-purple-300 bg-purple-500/10"
                            : "text-gray-300 hover:bg-white/[0.05]"
                        }`}
                      >
                        <TIcon size={11} className="text-purple-400" />
                        <span className="flex-1 text-left">{tab.label}</span>
                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ============ AI 超市产品货架（v5.0 新增默认视图） ============ */}
        {activeSection === "products" && <AIMarketplacePanel />}

        {/* ============ Agent Mesh 管控台（经验飞轮 + Agent 编排层） ============ */}
        {activeSection === "agentmesh" && <AgentMeshConsole />}

        {/* Runtime Catalog 区块（保留现有功能） */}
        {activeSection === "catalog" && (
        <>
        {/* KPI 行 */}
        <div className="grid grid-cols-4 gap-2 px-4 py-3 border-b border-white/[0.06]">
          <KpiCell label="产品总数" value={String(kpi.total)} color="#A78BFA" />
          <KpiCell label="运行中" value={String(kpi.running)} color="#10B981" />
          <KpiCell
            label="预期年收入"
            value={`¥${(kpi.totalRevenue / 10000).toFixed(1)}万`}
            color="#34D399"
          />
          <KpiCell label="平均进度" value={`${kpi.avgProgress}%`} color="#38BDF8" />
        </div>

        {/* Catalog 网格 */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-3 px-3 py-2 rounded border border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-300 font-mono">
              ⚠ {error}（已降级到静态 Catalog）
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {catalog.map((item, idx) => (
              <CatalogCard
                key={item.productId}
                item={item}
                index={idx}
                launching={launching === item.productId}
                onLaunch={() => launchProduct(item)}
                onRefresh={() => refreshProduct(item)}
                refreshing={refreshing}
                available={runtimeAvailability[item.productId] === true}
                health={runtimeHealth[item.productId]}
                onProbe={() => probeRuntime(item)}
                probing={
                  probe?.item.productId === item.productId && probe.loading
                }
              />
            ))}
          </div>
        </div>
        </>
        )}

        {/* 项目看板区块（P0-功能1） */}
        {activeSection === "dashboard" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ProjectDashboard />
          </div>
        )}

        {/* 任务看板区块（P0-功能2） */}
        {activeSection === "tasks" && (
          <div className="flex-1 overflow-y-auto p-4">
            <TaskBoard />
          </div>
        )}

        {/* AI项目助理区块（P0-功能3） */}
        {activeSection === "assistant" && (
          <div className="flex-1 overflow-hidden p-4">
            <AIProjectAssistant />
          </div>
        )}

        {/* P2 任务：站内量化工作台（实时行情 + 外部工作站入口） */}
        {activeSection === "quant" && (
          <div className="flex-1 overflow-y-auto p-4">
            <QuantTradingPanel />
          </div>
        )}

        {/* 资源管理区块（P1-功能4） */}
        {activeSection === "resources" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ResourceManager />
          </div>
        )}

        {/* 风险管理区块（P1-功能5） */}
        {activeSection === "risks" && (
          <div className="flex-1 overflow-y-auto p-4">
            <RiskManager />
          </div>
        )}

        {/* 质量管理区块（P1-功能6） */}
        {activeSection === "quality" && (
          <div className="flex-1 overflow-y-auto p-4">
            <QualityManager />
          </div>
        )}

        {/* 项目文档区块（P1-功能7） */}
        {activeSection === "docs" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ProjectDocs />
          </div>
        )}

        {/* P2-功能8：项目组合管理 */}
        {activeSection === "portfolio" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ProjectPortfolioManager />
          </div>
        )}

        {/* P2-功能9：战略对齐 */}
        {activeSection === "alignment" && (
          <div className="flex-1 overflow-y-auto p-4">
            <StrategicAlignment />
          </div>
        )}

        {/* P2-功能10：项目复盘与持续改进 */}
        {activeSection === "retrospective" && (
          <div className="flex-1 overflow-y-auto p-4">
            <ProjectRetrospective />
          </div>
        )}
      </motion.div>

      {/* 产品详情 Modal */}
      <AnimatePresence>
        {preview && (
          <ProductPreviewModal
            preview={preview}
            onClose={() => setPreview(null)}
          />
        )}
      </AnimatePresence>

      {/* 任务2：Runtime 实测 Modal */}
      <AnimatePresence>
        {probe && (
          <RuntimeProbeModal
            probe={probe}
            onClose={() => setProbe(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// KPI 单元格
// ============================================
function KpiCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div
        className="text-[20px] font-mono font-semibold mt-0.5"
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

// ============================================
// Catalog 卡片
// ============================================
function CatalogCard({
  item,
  index,
  launching,
  refreshing,
  onLaunch,
  onRefresh,
  available,
  health,
  onProbe,
  probing,
}: {
  item: CatalogItem;
  index: number;
  launching: boolean;
  refreshing: boolean;
  onLaunch: () => void;
  onRefresh: () => void;
  available: boolean;
  health?: RuntimeHealth;
  onProbe: () => void;
  probing: boolean;
}) {
  // 防御性兜底：防止 config 查表返回 undefined 后访问属性崩溃
  const categoryConfig = CATEGORY_CONFIG[item.category] ?? CATEGORY_CONFIG.saas;
  const statusConfig = STATUS_CONFIG[item.runtimeStatus] ?? STATUS_CONFIG.planning;
  const riskConfig = RISK_CONFIG[item.riskLevel] ?? RISK_CONFIG.medium;
  const CategoryIcon = categoryConfig.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      className="group relative rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.12] backdrop-blur-sm overflow-hidden transition-all"
    >
      {/* 顶部色带 */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: categoryConfig.color }}
      />

      {/* 任务3：左上角 health 色条（颜色根据 RuntimeInfo.health） */}
      {health && (
        <div
          className="absolute top-0 left-0 w-1 h-8 z-10"
          style={{ background: HEALTH_COLOR[health] }}
          title={`Health: ${HEALTH_LABEL[health]}`}
        />
      )}

      <div className="p-4">
        {/* 头部 */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center border"
              style={{
                background: `${categoryConfig.color}1a`,
                borderColor: `${categoryConfig.color}33`,
              }}
            >
              <CategoryIcon size={16} style={{ color: categoryConfig.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-[14px] font-semibold text-white tracking-tight">
                  {item.displayName}
                </h3>
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono ${statusConfig.dotColor}`}
                >
                  <span className={`w-1 h-1 rounded-full ${statusConfig.dotColor}`} />
                  <span className="text-white/80">{statusConfig.label}</span>
                </span>
                {/* 任务1：Runtime 可调用标记徽章 */}
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border ${
                    available
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : "bg-gray-500/10 text-gray-500 border-gray-500/20"
                  }`}
                  title={available ? "Runtime 已注册，可调用" : "Runtime 未注册"}
                >
                  <span
                    className={`w-1 h-1 rounded-full ${
                      available ? "bg-emerald-400" : "bg-gray-500"
                    }`}
                  />
                  {available ? "可调用" : "未注册"}
                </span>
              </div>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
                {item.subtitle}
              </p>
            </div>
          </div>
          <span
            className="text-[9px] font-mono px-1.5 py-0.5 rounded"
            style={{
              background: `${categoryConfig.color}1a`,
              color: categoryConfig.color,
            }}
          >
            {categoryConfig.label}
          </span>
        </div>

        {/* 描述 */}
        <p className="text-[11px] text-gray-400 leading-relaxed mb-3 line-clamp-2">
          {item.description}
        </p>

        {/* 进度 */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              进度
            </span>
            <span className="text-[10px] font-mono text-gray-300">
              {item.progress}%
            </span>
          </div>
          <div className="h-1 rounded-full bg-white/[0.04] overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${item.progress}%` }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.06 }}
              className="h-full rounded-full"
              style={{ background: categoryConfig.color }}
            />
          </div>
        </div>

        {/* 能力 chips */}
        {item.capabilities.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1">
            {item.capabilities.slice(0, 4).map((cap) => (
              <span
                key={cap}
                className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-white/[0.04] text-gray-400 border border-white/[0.04]"
              >
                {cap}
              </span>
            ))}
            {item.capabilities.length > 4 && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-500">
                +{item.capabilities.length - 4}
              </span>
            )}
          </div>
        )}

        {/* 元信息 */}
        <div className="flex items-center justify-between mb-3 text-[10px] font-mono text-gray-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <User size={10} />
              {item.owner}
            </span>
            <span
              className="flex items-center gap-1"
              style={{ color: `var(--${riskConfig.color}-400)` }}
            >
              <Shield size={10} />
              {riskConfig.label}
            </span>
          </div>
          <span className="text-emerald-400/80">
            ¥{(item.expectedRevenue / 10000).toFixed(1)}万/年
          </span>
        </div>

        {/* 里程碑（最近 1 个） */}
        {item.milestones.length > 0 && (
          <div className="mb-3 px-2 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]">
            <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-0.5">
              当前里程碑
            </div>
            <div className="text-[11px] text-gray-300 truncate">
              {item.milestones[0].name}
              <span className="text-gray-600 ml-1.5">
                · {item.milestones[0].dueDate}
              </span>
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={onLaunch}
            disabled={launching}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all disabled:opacity-50"
            style={{
              background: `${categoryConfig.color}1a`,
              color: categoryConfig.color,
              border: `1px solid ${categoryConfig.color}33`,
            }}
          >
            {launching ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <ArrowRight size={12} />
            )}
            进入产品
          </button>
          {/* 任务2：实测按钮 - 调用 getInfo() 获取真实 RuntimeInfo */}
          <button
            onClick={onProbe}
            disabled={probing}
            className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-white/[0.06] hover:bg-white/[0.04] text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 text-[11px]"
            title="实测 Runtime 真实状态"
          >
            {probing ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Activity size={11} />
            )}
            实测
          </button>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="px-2 py-1.5 rounded-lg border border-white/[0.06] hover:bg-white/[0.04] text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-50"
            title="重新加载 Vault 数据"
          >
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// 产品预览 Modal
// ============================================
function ProductPreviewModal({
  preview,
  onClose,
}: {
  preview: { item: CatalogItem; content: string; loading: boolean };
  onClose: () => void;
}) {
  const { item, content, loading } = preview;
  const categoryConfig = CATEGORY_CONFIG[item.category];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-xl border border-white/[0.08] bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]"
          style={{
            background: `linear-gradient(90deg, ${categoryConfig.color}10 0%, transparent 100%)`,
          }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center border"
              style={{
                background: `${categoryConfig.color}1a`,
                borderColor: `${categoryConfig.color}33`,
              }}
            >
              {(() => {
                const Icon = categoryConfig.icon;
                return <Icon size={14} style={{ color: categoryConfig.color }} />;
              })()}
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-white">
                {item.displayName}
              </h3>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                {item.subtitle} · Runtime Catalog
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 size={20} className="animate-spin text-gray-500" />
              <span className="ml-2 text-[12px] font-mono text-gray-500">
                正在加载产品详情...
              </span>
            </div>
          ) : (
            <>
              {/* 元信息栏 */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <MetaCell label="负责人" value={item.owner} />
                <MetaCell label="进度" value={`${item.progress}%`} />
                <MetaCell
                  label="预期年收入"
                  value={`¥${(item.expectedRevenue / 10000).toFixed(1)}万`}
                />
                <MetaCell label="风险" value={RISK_CONFIG[item.riskLevel].label} />
              </div>

              {/* 能力 */}
              {item.capabilities.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                    产品能力
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {item.capabilities.map((cap) => (
                      <span
                        key={cap}
                        className="px-2 py-0.5 rounded text-[10px] font-mono bg-white/[0.04] text-gray-300 border border-white/[0.06]"
                      >
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 里程碑 */}
              {item.milestones.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1.5">
                    里程碑
                  </div>
                  <div className="space-y-1">
                    {item.milestones.map((m, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-[11px] text-gray-300"
                      >
                        {m.status === "completed" ? (
                          <CheckCircle2 size={11} className="text-emerald-400" />
                        ) : m.status === "in_progress" ? (
                          <PlayCircle size={11} className="text-amber-400" />
                        ) : (
                          <CircleDashed size={11} className="text-gray-500" />
                        )}
                        <span className="flex-1">{m.name}</span>
                        <span className="font-mono text-gray-500 text-[10px]">
                          {m.dueDate}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Vault 项目文件内容 */}
              <div className="mb-2">
                <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <ExternalLink size={10} />
                  Vault 项目文档
                  {item.vaultPath && (
                    <span className="text-gray-600 normal-case tracking-normal">
                      · {item.vaultPath}
                    </span>
                  )}
                </div>
                <pre className="px-3 py-2 rounded-lg bg-black/30 border border-white/[0.04] text-[11px] text-gray-300 font-mono whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
                  {content || "暂无文档内容"}
                </pre>
              </div>
            </>
          )}
        </div>

        {/* 底部说明 */}
        <div className="px-5 py-2.5 border-t border-white/[0.06] bg-white/[0.01]">
          <p className="text-[10px] font-mono text-gray-600">
            * Program A 阶段：Runtime Catalog stub · 后续将接入完整产品 Dashboard
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="text-[12px] font-mono text-gray-200 mt-0.5">{value}</div>
    </div>
  );
}

// ============================================
// 任务2：Runtime 实测 Modal
// 展示 RuntimeManager.getRuntime(name).getInfo() 返回的真实 RuntimeInfo
// ============================================
function RuntimeProbeModal({
  probe,
  onClose,
}: {
  probe: {
    item: CatalogItem;
    loading: boolean;
    info: RuntimeInfo | null;
    error: string | null;
  };
  onClose: () => void;
}) {
  const { item, loading, info, error } = probe;
  const health = info?.health;
  // 实测未完成时默认灰色，完成后按 health 取色
  const healthColor = health ? HEALTH_COLOR[health] : "#9CA3AF";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md flex flex-col rounded-xl border border-white/[0.08] bg-[#0F1623] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]"
          style={{
            background: `linear-gradient(90deg, ${healthColor}10 0%, transparent 100%)`,
          }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: healthColor }}
            />
            <div>
              <h3 className="text-[14px] font-semibold text-white">
                {item.displayName} · Runtime 实测
              </h3>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                {item.runtimeName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/[0.06] text-gray-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 p-5">
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <Loader2 size={20} className="animate-spin text-gray-500" />
              <span className="ml-2 text-[12px] font-mono text-gray-500">
                正在调用 getInfo()...
              </span>
            </div>
          ) : error ? (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-[11px] text-red-300 font-mono">
              ⚠ 实测失败：{error}
            </div>
          ) : info ? (
            <div className="space-y-2">
              {/* Health 行 */}
              <ProbeRow
                label="Health"
                value={HEALTH_LABEL[info.health]}
                valueColor={healthColor}
                dotColor={healthColor}
              />
              {/* Status 行 */}
              <ProbeRow
                label="Status"
                value={RUNTIME_STATUS_LABEL[info.status]}
              />
              {/* Version 行 */}
              <ProbeRow
                label="Version"
                value={info.version || "—"}
              />
              {/* Uptime 行 */}
              <ProbeRow
                label="Uptime"
                value={
                  typeof info.uptime === "number"
                    ? `${info.uptime}s`
                    : "—"
                }
              />
            </div>
          ) : null}
        </div>

        {/* 底部说明 */}
        <div className="px-5 py-2.5 border-t border-white/[0.06] bg-white/[0.01]">
          <p className="text-[10px] font-mono text-gray-600">
            * 调用 RuntimeManager.getRuntime(name).getInfo() 实测
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// 实测 Modal 数据行
// ============================================
function ProbeRow({
  label,
  value,
  valueColor,
  dotColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
  dotColor?: string;
}) {
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <span className="text-[11px] font-mono text-gray-500 uppercase tracking-wider">
        {label}
      </span>
      <span
        className="flex items-center gap-1.5 text-[12px] font-mono"
        style={{ color: valueColor || "#E5E7EB" }}
      >
        {dotColor && (
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: dotColor }}
          />
        )}
        {value}
      </span>
    </div>
  );
}
